/**
 * File Processing Configuration
 *
 * Single source of truth for per-file-type processing behaviour.
 *
 * To add support for a new file type:
 * 1. Extend getProcessingType() in fileTypeDetection.ts to return the new key
 * 2. Add an entry here
 *
 * Backend upload, extraction, and batch boundaries call getFileConfig() and
 * pick up new behaviour automatically. Mobile Quick Capture sends raw user
 * intent; the backend upload boundary resolves policy.
 */
import { getProcessingType } from '../utils/fileTypeDetection';
/**
 * Derived helper: should note creation be queued immediately after upload?
 *
 * true  — autoConvert is 'always', meaning extraction always yields content
 *         (transcription, DOCX) — safe to enqueue right away.
 * false — autoConvert is conditional; wait for the extract-content job to
 *         decide (images may have no text; PDFs stay as attachments).
 *
 * Not stored in the config table to prevent inconsistency between autoConvert
 * and a redundant boolean — derive it at the call site instead.
 */
export function shouldEagerEnqueue(config) {
    return config.autoConvert === 'always';
}
export const FILE_PROCESSING_CONFIG = {
    /** Images — OCR may or may not find text */
    ocr: {
        autoConvert: 'when-text-found',
        titlePolicy: 'ai-generated',
        supportsThumbnail: true,
    },
    /** Audio — transcription USUALLY produces a transcript. Silent, music-only or
     *  pocket recordings legitimately produce none, and `titlePolicy` being
     *  'ai-generated' means such an attachment has no title to fall back on. Any
     *  caller acting on this policy must handle "no content to title" as a normal
     *  outcome — treating it as a failure is what made silent audio retry until
     *  Cloud Tasks gave up (2026-09-08). */
    transcription: {
        autoConvert: 'always',
        titlePolicy: 'ai-generated',
        supportsThumbnail: false,
    },
    /** Video — keep as attachment for native <video> playback.
     *
     *  `supportsThumbnail: false` does NOT mean a video card has no preview
     *  image. It means the SHARP path does not produce it, which is the only
     *  question this flag decides. This read `true` with a comment saying
     *  "thumbnails are generated server-side (first frame)", and both halves of
     *  that were misleading: `generateAttachmentThumbnail` ANDs this flag with
     *  `isProcessableImage(mimetype)`, which is false for every video MIME, so
     *  the branch was unreachable and no video ever got a thumbnail from it.
     *
     *  The first frame is real, but it comes from elsewhere: the transcode
     *  worker runs ffmpeg (`buildPosterArgs`) and `completeTranscode` stores the
     *  result as `content.posterPath`. The read path picks that up in
     *  `deriveThumbnailStoragePath`, so a video card and an image card still
     *  share one visual treatment — via two different producers.
     *
     *  Transcription of the audio track is intentionally NOT auto-queued for now
     *  since the transcription pipeline accepts audio MIMEs only. Future work can
     *  flip autoConvert to 'always' once the pipeline grows a video → audio
     *  extraction step. */
    video: {
        autoConvert: 'never',
        titlePolicy: 'filename',
        supportsThumbnail: false,
    },
    /** Word documents — conversion always produces markdown */
    docx: {
        autoConvert: 'always',
        titlePolicy: 'filename',
        supportsThumbnail: false,
    },
    /** PDFs — extraction enriches the attachment but preserves the PDF viewer */
    pdf: {
        autoConvert: 'never',
        titlePolicy: 'filename',
        supportsThumbnail: false,
    },
    /** Plain text / markdown — content read directly, no LLM; always becomes a note */
    text: {
        autoConvert: 'always',
        titlePolicy: 'filename',
        supportsThumbnail: false,
    },
    /** Source code — stays an attachment; extraction stores a language-tagged
     * fence so search and the agent can read it. Never becomes a note. */
    code: {
        autoConvert: 'never',
        titlePolicy: 'filename',
        supportsThumbnail: false,
    },
    /** Everything else: spreadsheets, archives, unknown types */
    other: {
        autoConvert: 'never',
        titlePolicy: 'filename',
        supportsThumbnail: false,
    },
};
/**
 * Look up processing behaviour for a file by name and MIME type.
 *
 * This is the single call-site for all file-type decisions across the app.
 * Callers should not write their own isMediaFile / !isPhoto checks.
 */
export function getFileConfig(fileName, mimeType) {
    // getProcessingType returns Exclude<ProcessingTypeKey, 'other'> | null
    // null maps to 'other' — no cast needed
    const type = getProcessingType(fileName, mimeType) ?? 'other';
    return FILE_PROCESSING_CONFIG[type];
}
export function resolveUploadTitleIntent(fileName, mimeType, userTitle) {
    const explicitTitle = userTitle?.trim();
    if (explicitTitle) {
        return { title: explicitTitle, source: 'user' };
    }
    return getFileConfig(fileName, mimeType).titlePolicy === 'ai-generated'
        ? { title: null, source: 'ai-generated' }
        : { title: fileName, source: 'filename' };
}
/**
 * What the SURFACE is doing with a file it just uploaded.
 *
 * Declared by the caller, never inferred, and read by exactly one consumer:
 * `dispatchPostUpload`, the single place that decides what happens to a
 * freshly-landed attachment.
 *
 * THREE, because two could not say what the protocol importer meant. That
 * surface uploads a document, mines its text and builds a PROTOCOL from it,
 * and the only way to say "do not also file a note" was `importAsNotes:
 * false` - which returns before extraction as well, so it would have taken
 * the text with it. Every PDF protocol import would have lost its source
 * text, and with it the quote-provenance check and the refusal guard, both
 * of which read the attachment's extracted markdown. Silently: the import
 * still succeeds, it just stops being checkable.
 *
 * So the boolean was not a flag with a missing case, it was two decisions
 * wearing one name. These separate them.
 */
export const UPLOAD_INTENTS = ['attach', 'import', 'source'];
/**
 * - `attach`  store it against its parent. No extraction, no note. (Equipment
 *             is the standing exception: its handler indexes a PDF in place.)
 * - `import`  the dashboard / collection flow. Extract, and file a note for
 *             the types that always yield one.
 * - `source`  the file is raw material for something the CALLER is already
 *             building. Extract it, so the text is there to search, quote and
 *             verify against, and do not file the EAGER note: the caller owns
 *             the outcome.
 *
 * WHAT `source` DOES NOT COVER, stated because the obvious reading is wrong.
 * It suppresses the note this dispatch would file. It does NOT reach the
 * SECOND note route: an `autoConvert: 'when-text-found'` type (images) has no
 * eager note to suppress, and its note is routed several async hops later by
 * `shouldRouteAutomaticOcrNote`, which gates on `suppressAutoConvert` /
 * `alreadyParented` / `isVisualReferenceImage` and never sees an intent. So a
 * PNG uploaded as `source` still gets a note when OCR finds text.
 *
 * Nothing reaches that today - the only `source` surface accepts .pdf and
 * .docx - and threading the intent onto the extraction payload is a bigger
 * change than this one. Saying so is the point: a contract declarable at a
 * public endpoint should not claim more than it enforces.
 */
export const UPLOAD_INTENT_DEFAULT = 'import';
/** The sidecar and the multipart body both used to carry a boolean. Read the
 * intent where a caller declared one, and fall back to what the boolean meant,
 * so an upload reserved before this shipped still finalizes correctly. */
export function resolveUploadIntent(declared, importAsNotes, onUnrecognised) {
    if (declared && UPLOAD_INTENTS.includes(declared)) {
        return declared;
    }
    // A value that is present but not a member is a DIFFERENT fact from an
    // absent one, and falling back on both silently makes a typo in a stored
    // sidecar indistinguishable from an upload that predates the field. Both
    // request doors reject an unknown value loudly; this door could not, so it
    // says so instead (rule 12).
    if (declared)
        onUnrecognised?.(declared);
    return importAsNotes ? 'import' : 'attach';
}
//# sourceMappingURL=fileProcessing.js.map